import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { adminCategories } from "@/mocks/admin";
import type { Category } from "@/mocks/admin";

function rowToCategory(row: Record<string, unknown>): Category {
  return {
    id: row.id as string,
    name: row.name as string,
    slug: row.slug as string,
    description: (row.description as string) || "",
    color: (row.color as string) || "#8B0000",
    imageUrl: row.image_url as string | undefined,
    articleCount: (row.article_count as number) || 0,
    isActive: row.is_active as boolean,
    order: (row.sort_order as number) || 0,
    showInMenu: row.show_in_menu as boolean,
    showOnHomepage: row.show_on_homepage as boolean,
    homepageOrder: (row.homepage_order as number) || 0,
    seoTitle: row.seo_title as string | undefined,
    seoDescription: row.seo_description as string | undefined,
    subcategories: (row.subcategories as Category["subcategories"]) || [],
  };
}

export function useCategoriesPublic() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuCategories, setMenuCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });

    if (error || !data || data.length === 0) {
      // Supabase not connected or empty → fallback to mock
      const all = adminCategories
        .filter((c) => c.isActive)
        .sort((a, b) => a.order - b.order);
      const menu = all.filter((c) => c.showInMenu);
      setCategories(all);
      setMenuCategories(menu);
    } else {
      const parsed = (data as Record<string, unknown>[]).map(rowToCategory);
      const all = parsed
        .filter((c) => c.isActive)
        .sort((a, b) => a.order - b.order);
      const menu = all.filter((c) => c.showInMenu);
      setCategories(all);
      setMenuCategories(menu);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return { categories, menuCategories, loading, refetch: fetchCategories };
}